import React, { useState, useEffect, useContext } from "react";
import Styles from "../styles/UsersList.module.css";
import { userAPI } from "../services/api";
import { UserContext } from "./context/user/UserContext";
import { useDispatch, useSelector } from "react-redux";
import {
  setUsers,
  replaceUsers,
  removeUser,
} from "../redux/slices/UsersSlice";
import UserContainer from "./UserContainer";
import Loader from "./Loader";
import Toast from "./Toast";
import ConfirmModal from "./ConfirmModal";

const UserList = () => {
  const { userData } = useContext(UserContext);
  const token = userData?.token;
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users.users);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    userId: null,
  });

  useEffect(() => {
    if (!token) return;

    let active = true;
    setLoading(true);

    userAPI
      .getAllUsers(token, page, query)
      .then((response) => {
        if (!active) return;
        setTotalPages(Math.max(1, Number(response.totalPages) || 1));
        if (page === 1) {
          dispatch(replaceUsers(response.data || []));
        } else {
          dispatch(setUsers(response.data || []));
        }
      })
      .catch((error) => {
        if (!active) return;
        setToast({
          message: `Failed to load users: ${error.message}`,
          type: "error",
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [dispatch, page, query, token]);

  const applySearch = () => {
    setPage(1);
    setQuery(search.trim());
  };

  const clearSearch = () => {
    setSearch("");
    setQuery("");
    setPage(1);
  };

  const handleDeleteClick = (userId) => {
    setConfirmModal({ isOpen: true, userId });
  };

  const handleDeleteConfirm = async () => {
    const userId = confirmModal.userId;
    setToast({ message: "Deleting user...", type: "info" });

    try {
      await userAPI.deleteUser(token, userId);
      dispatch(removeUser(userId));
      setToast({ message: "User deleted successfully!", type: "success" });
    } catch (error) {
      setToast({
        message: `Failed to delete user: ${error.message}`,
        type: "error",
      });
    }
  };

  return (
    <div className={Styles.container}>
      <div className={Styles.heading}>
        <h2>USERS</h2>
      </div>

      <div className={Styles.inputContainer}>
        <input
          type="text"
          className={Styles.input}
          value={search}
          placeholder="Search username or email"
          aria-label="Search users"
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") applySearch();
          }}
        />
        {search && (
          <button
            type="button"
            className={Styles.clear_button}
            onClick={clearSearch}
          >
            Clear
          </button>
        )}
        <button
          type="button"
          className={Styles.search_button}
          onClick={applySearch}
        >
          Search
        </button>
      </div>

      <div className={Styles.user_list}>
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>Username</th>
              <th>User ID</th>
              <th>Email</th>
              <th>Role</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <UserContainer
                key={user.id}
                user={user}
                handleDelete={handleDeleteClick}
                index={index}
              />
            ))}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan="6" className={Styles.empty_state}>
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={Styles.pagination}>
        <button
          type="button"
          onClick={() => setPage((value) => Math.max(1, value - 1))}
          disabled={loading || page <= 1}
        >
          Previous
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
          disabled={loading || page >= totalPages}
        >
          Next
        </button>
      </div>

      {loading && <Loader text="Loading users" />}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, userId: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete User?"
        message="Are you sure you want to delete this user? This action cannot be undone and will permanently remove the user and all their data."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default UserList;
